/**
 * useAdminTable (T28.5 Phase 2) — the admin list **query-state** hook.
 *
 * Rewired from the T28.3 client-side sort/filter over already-loaded rows into a
 * holder of the SERVER query state: `{ sort, filters, page, pageSize }` plus the
 * setters the header + filter row + pager bind to. The page no longer filters
 * rows itself — it builds the server query from `table.params` and refetches on
 * change; sort/filter/paging now operate over the **whole** set (the T28.5
 * server contract: `?limit=&offset=&sort=key:dir&filter.<col>=value` → its array
 * key + a real filtered `total`).
 *
 * Two derived values matter to callers:
 *   - `params` — the ready query inputs (`sort`, `filters`, `limit`, `offset`),
 *     with the text filters **debounced** (~250 ms) so typing fires one fetch per
 *     pause. Sort, facet, page and page-size changes apply immediately.
 *   - the raw `textValue(key)` — the live input value (un-debounced) so the field
 *     stays responsive while the fetch waits on the debounced copy.
 *
 * Page resets to 1 whenever a sort, a filter (text or facet), or the page size
 * changes — a narrower/re-sorted set starts at its first page.
 */

import { useMemo, useState, useCallback, useEffect } from 'react';

/** Sort direction for a single column. */
export type SortDir = 'asc' | 'desc';

/**
 * The page/page-size subset of the table state that `AdminPager` binds to. Both
 * the full server `useAdminTable` state and the client-side `useClientPager`
 * satisfy this, so the same pager renders for server-paged lists and for the
 * store-detail sub-tables (paged in memory over a bounded loaded set).
 */
export interface AdminPagerState {
  /** Current page (1-based). */
  page: number;
  /** Set the current page. */
  setPage: (page: number) => void;
  /** Current page size. */
  pageSize: number;
  /** Set the page size (resets to page 1). */
  setPageSize: (size: number) => void;
}

/**
 * One column in an admin list table. `key` is BOTH the display key and the
 * **server sort/filter key** — it must match the Phase-1 allow-list 1:1 (e.g.
 * `name`, `store_count`, `normalized_name`). `sortValue` / `filterText` /
 * `facetValue` from the T28.3 client model are gone (the server sorts/filters
 * now); a faceted column declares its candidate values via `facetOptions` since
 * the loaded page no longer holds the full set to derive them from.
 */
export interface AdminColumn {
  /** Stable identifier AND the server sort/filter key (Phase-1 allow-list 1:1). */
  key: string;
  /** Visible header label. */
  label: string;
  /**
   * Optional native tooltip (`title` attribute) on the header cell — for a
   * column whose short label needs a one-line disambiguation on hover (e.g.
   * transitional vocabulary that will rename at a future switchover). Purely
   * additive: a column that omits it renders exactly as before (T65.14.1
   * owner walkthrough follow-up).
   */
  title?: string;
  /** Right-align numeric columns (`.admin-num`). */
  numeric?: boolean;
  /** This column's header is a sort button (cycles asc -> desc -> none). */
  sortable?: boolean;
  /** Free-text filter input under the header (emits `filter.<key>`). */
  textFilter?: boolean;
  /**
   * Faceted multi-select filter under the header — for columns whose values come
   * from a small fixed set (e.g. Country: CA | US | —). Emits `filter.<key>` as a
   * comma-separated value list. The candidate values come from `facetOptions`.
   */
  facet?: boolean;
  /**
   * The candidate facet values shown in the popover (in display order). Required
   * when `facet` is set — the page supplies them (a fixed set, or distinct values
   * it learned from a meta fetch), since the loaded page is only one page now.
   */
  facetOptions?: string[];
}

/** Per-column filter state: a string for text filters, a value list for facets. */
type FilterState = Record<string, string | string[]>;

interface SortState {
  key: string | null;
  dir: SortDir;
}

/** The server query inputs a page maps onto its fetch (sort + filters + paging). */
export interface AdminQueryParams {
  /** Active sort, or null (the server falls back to the list default). */
  sort: SortState;
  /** Active per-column filters (text values trimmed, facets as value lists). */
  filters: FilterState;
  /** Page size (rows per page). */
  limit: number;
  /** Row offset for the current page (`(page - 1) * pageSize`). */
  offset: number;
}

export interface AdminTableState extends AdminPagerState {
  /** The active sort, for header caret + `aria-sort`. */
  sort: SortState;
  /** Cycle a column's sort asc -> desc -> none. */
  cycleSort: (key: string) => void;
  /** `aria-sort` value for a column's `<th>`. */
  ariaSort: (key: string) => 'ascending' | 'descending' | 'none';
  /** Current (live, un-debounced) text-filter value for a column. */
  textValue: (key: string) => string;
  /** Set a column's text filter. */
  setText: (key: string, value: string) => void;
  /** Candidate facet values for a column (from the column's `facetOptions`). */
  facetValues: (key: string) => string[];
  /** Currently-selected facet values for a column. */
  facetSelected: (key: string) => string[];
  /** Toggle one facet value on/off for a column. */
  toggleFacet: (key: string, value: string) => void;
  /** Clear all selected facet values for a column. */
  clearFacet: (key: string) => void;
  /** The debounced server query inputs the page builds its fetch from. */
  params: AdminQueryParams;
  // --- Row multi-select (T28.12, additive) ---------------------------------
  // For the bulk-delete cleanup mode. The set holds selected row ids; it
  // survives within a page but CLEARS whenever a sort, a filter, the page, or
  // the page size changes (a re-sorted / re-paged set is a different set of
  // rows, so a stale selection would be misleading).
  /** The selected row ids (cleanup mode). */
  selectedIds: Set<string>;
  /** Is this id currently selected? */
  isSelected: (id: string) => boolean;
  /** Toggle one row's selection. */
  toggleSelected: (id: string) => void;
  /** Add every id on the current page to the selection (select-all-on-page). */
  selectAll: (ids: string[]) => void;
  /** Remove every id in the list from the selection (deselect-all-on-page). */
  deselectAll: (ids: string[]) => void;
  /** Clear the whole selection. */
  clearSelection: () => void;
}

/** The page-size options the selector offers; the first is the sensible default. */
export const ADMIN_PAGE_SIZES = [25, 50, 100] as const;

/**
 * @param columns the column model — which columns sort/filter and how (keys map
 *   to the Phase-1 server allow-list 1:1).
 * @param options.defaultPageSize the initial page size (default 50).
 * @param options.debounceMs the text-filter debounce (default 250 ms).
 * @param options.initialSort the initial sort state — seeds the list's default
 *   column + direction so the first fetch carries an explicit `sort` param
 *   (else the server falls back to its own default). Defaults to no sort
 *   (`{ key: null, dir: 'asc' }`), preserving today's behavior for callers that
 *   don't pass it.
 */
export function useAdminTable(
  columns: AdminColumn[],
  options: {
    defaultPageSize?: number;
    debounceMs?: number;
    initialSort?: { key: string; dir: SortDir };
  } = {},
): AdminTableState {
  const defaultPageSize = options.defaultPageSize ?? 50;
  const debounceMs = options.debounceMs ?? 250;

  const [sort, setSort] = useState<SortState>(
    options.initialSort ?? { key: null, dir: 'asc' },
  );
  const [filters, setFilters] = useState<FilterState>({});
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);

  // Debounced copy of the filter map — only the text filters need debouncing,
  // but debouncing the whole map is simplest and a facet toggle re-settles after
  // the same short delay (negligible). The page reads `params` off this.
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>({});
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedFilters(filters), debounceMs);
    return () => clearTimeout(handle);
  }, [filters, debounceMs]);

  const colByKey = useMemo(() => {
    const m = new Map<string, AdminColumn>();
    for (const c of columns) m.set(c.key, c);
    return m;
  }, [columns]);

  // Reset to the first page on any sort change (a re-sorted set starts at page 1).
  const cycleSort = useCallback((key: string) => {
    setPageState(1);
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' }; // desc -> none
    });
  }, []);

  const ariaSort = useCallback(
    (key: string): 'ascending' | 'descending' | 'none' => {
      if (sort.key !== key) return 'none';
      return sort.dir === 'asc' ? 'ascending' : 'descending';
    },
    [sort],
  );

  const textValue = useCallback(
    (key: string) => {
      const v = filters[key];
      return typeof v === 'string' ? v : '';
    },
    [filters],
  );

  // A text-filter edit resets to page 1 (a narrower set starts at its first page).
  const setText = useCallback((key: string, value: string) => {
    setPageState(1);
    setFilters((f) => ({ ...f, [key]: value }));
  }, []);

  const facetSelected = useCallback(
    (key: string) => {
      const v = filters[key];
      return Array.isArray(v) ? v : [];
    },
    [filters],
  );

  const facetValues = useCallback(
    (key: string) => colByKey.get(key)?.facetOptions ?? [],
    [colByKey],
  );

  const toggleFacet = useCallback((key: string, value: string) => {
    setPageState(1);
    setFilters((f) => {
      const cur = Array.isArray(f[key]) ? (f[key] as string[]) : [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...f, [key]: next };
    });
  }, []);

  const clearFacet = useCallback((key: string) => {
    setPageState(1);
    setFilters((f) => ({ ...f, [key]: [] }));
  }, []);

  const setPage = useCallback((p: number) => setPageState(Math.max(1, Math.floor(p))), []);

  const setPageSize = useCallback((size: number) => {
    setPageState(1);
    setPageSizeState(size);
  }, []);

  const params = useMemo<AdminQueryParams>(
    () => ({
      sort,
      filters: debouncedFilters,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [sort, debouncedFilters, page, pageSize],
  );

  // --- Row multi-select (T28.12) -------------------------------------------
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  // A different page / sort / filter is a different set of rows — clear the
  // selection so a stale id can't ride along into a bulk action. Keyed on the
  // LIVE sort/filter/page state (not the debounced `params`) so the selection
  // clears the instant the operator changes a control.
  useEffect(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [sort, filters, page, pageSize]);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  const deselectAll = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  return {
    sort,
    cycleSort,
    ariaSort,
    textValue,
    setText,
    facetValues,
    facetSelected,
    toggleFacet,
    clearFacet,
    page,
    setPage,
    pageSize,
    setPageSize,
    params,
    selectedIds,
    isSelected,
    toggleSelected,
    selectAll,
    deselectAll,
    clearSelection,
  };
}

/** What `useClientPager` returns — the pager state plus an in-memory slicer. */
export interface ClientPagerState extends AdminPagerState {
  /** The current page's slice of a loaded array (paged in memory). */
  slice: <T>(rows: T[]) => T[];
}

/**
 * Client-side paginator (T28.5 Phase 3) for the store-detail sub-tables. Unlike
 * `useAdminTable` (which builds a SERVER query), this pages a **fully loaded,
 * bounded** array in memory — the deals/offers resolver returns the whole
 * per-store set (e.g. 165 No Frills deals), so there's no second endpoint.
 *
 * Returns the same `{ page, pageSize, setPage, setPageSize }` shape `AdminPager`
 * binds to, plus `slice(rows)` that returns the current page's rows. The page is
 * clamped to the available range when `total` shrinks (e.g. switching tabs to a
 * shorter feed), so the slice never points past the end.
 *
 * @param total the loaded array's length (drives the page count + clamping).
 * @param defaultPageSize the initial page size (default 25 — sub-table sets are
 *   smaller than the catalog lists, and 25 keeps the detail page short).
 */
export function useClientPager(total: number, defaultPageSize = 25): ClientPagerState {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // Clamp into range — a shorter feed (after a tab switch) must not strand the
  // view on an empty trailing page.
  const current = Math.min(page, pageCount);

  const setPage = useCallback((p: number) => setPageState(Math.max(1, Math.floor(p))), []);
  const setPageSize = useCallback((size: number) => {
    setPageState(1);
    setPageSizeState(size);
  }, []);

  const slice = useCallback(
    <T,>(rows: T[]): T[] => {
      const start = (current - 1) * pageSize;
      return rows.slice(start, start + pageSize);
    },
    [current, pageSize],
  );

  return { page: current, setPage, pageSize, setPageSize, slice };
}

/**
 * A column for the in-memory client table (`useClientTable`, T28.8). It carries
 * everything an `AdminColumn` does (so `AdminTableHead` renders it identically)
 * plus a `value(row)` accessor: the hook sorts and filters on that value, while
 * the sub-table renders its own cell (which may differ — e.g. a column whose
 * cell lists the connected system products but whose `value` is the count or the
 * first name). For a faceted column, `facetOptions` may be omitted — the hook
 * derives the distinct values from the loaded rows when none are supplied.
 */
export interface ClientColumn<T> extends AdminColumn {
  /** The value the hook sorts + filters on (rendered cell is the sub-table's own). */
  value: (row: T) => string | number | null | undefined;
}

/** What `useClientTable` returns — the full table state plus the page's rows + filtered total. */
export interface ClientTableState<T> extends AdminTableState {
  /** The current page's rows after sort + filter + paginate (in memory). */
  rows: T[];
  /** The filtered total (rows that pass the filters, before paging). */
  total: number;
}

/** Coerce a column value to a comparable lower-cased string (null/undefined → ''). */
function valueToText(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v).toLowerCase();
}

/**
 * In-memory table (T28.8) for the admin detail sub-tables — the household's
 * products, a system product's offers, a store's deals/offers. Generalizes
 * `useClientPager` (paging only) into full sort + filter + paginate over a
 * **fully loaded, bounded** array, returning the same `AdminTableState` shape
 * the server-paged lists expose — so `AdminTableHead` (sortable headers + text /
 * facet filters) and `AdminPager` drive it unchanged, and a sub-table looks
 * identical to a catalog list without a second endpoint.
 *
 * The hook owns the sort/filter/page STATE; the actual sort/filter/slice runs in
 * memory here on each render via the columns' `value(row)` accessors:
 *   - sort: the active column's value, ascending/descending, numbers numerically
 *     and everything else as a lower-cased string;
 *   - text filter: case-insensitive substring on the column value;
 *   - facet filter: the column value (as text) must be one of the selected values;
 *   - paginate: the active page's slice.
 * `rows` is the current page; `total` is the filtered count (drives `AdminPager`).
 *
 * @param columns the client columns (each with a `value` accessor).
 * @param data the fully loaded, bounded array.
 * @param options.defaultPageSize initial page size (default 25 — matches the
 *   `useClientPager` sub-table default).
 * @param options.debounceMs text-filter debounce (default 0 — the filter is in
 *   memory, so there's no fetch to spare; typing applies immediately).
 */
export function useClientTable<T>(
  columns: ClientColumn<T>[],
  data: T[],
  options: { defaultPageSize?: number; debounceMs?: number } = {},
): ClientTableState<T> {
  const base = useAdminTable(columns, {
    defaultPageSize: options.defaultPageSize ?? 25,
    debounceMs: options.debounceMs ?? 0,
  });
  const { sort, pageSize } = base;

  // Read the LIVE (un-debounced) filter values straight off the base hook's
  // accessors rather than `params.filters`: in-memory filtering needs no
  // debounce, and reading the live values applies a keystroke / facet toggle on
  // the same render instead of after the base hook's debounce timer.
  const liveFilters = useMemo(() => {
    const m: Record<string, string | string[]> = {};
    for (const c of columns) {
      if (c.textFilter) m[c.key] = base.textValue(c.key);
      if (c.facet) m[c.key] = base.facetSelected(c.key);
    }
    return m;
    // base.textValue / base.facetSelected are stable callbacks keyed on the
    // filter state, so they change identity when a filter changes → re-derive.
  }, [columns, base.textValue, base.facetSelected]);

  const colByKey = useMemo(() => {
    const m = new Map<string, ClientColumn<T>>();
    for (const c of columns) m.set(c.key, c);
    return m;
  }, [columns]);

  // Facet candidate values: a column's explicit `facetOptions`, or the distinct
  // values learned from the loaded rows (sorted) when none were supplied.
  const derivedFacets = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const c of columns) {
      if (!c.facet || c.facetOptions) continue;
      const seen = new Set<string>();
      for (const row of data) {
        const v = c.value(row);
        const text = v === null || v === undefined ? '—' : String(v);
        seen.add(text);
      }
      m.set(c.key, [...seen].sort((a, b) => a.localeCompare(b)));
    }
    return m;
  }, [columns, data]);

  // Filter → sort → the filtered total. Paging is applied after.
  const filteredSorted = useMemo(() => {
    const filtered = data.filter((row) => {
      for (const [key, raw] of Object.entries(liveFilters)) {
        const col = colByKey.get(key);
        if (!col) continue;
        const cellText = valueToText(col.value(row));
        if (Array.isArray(raw)) {
          // Facet: the value (display form) must be one of the selected values.
          const selected = raw.filter((v) => v.trim() !== '');
          if (selected.length === 0) continue;
          const v = col.value(row);
          const display = v === null || v === undefined ? '—' : String(v);
          if (!selected.includes(display)) return false;
        } else if (typeof raw === 'string' && raw.trim() !== '') {
          // Text: case-insensitive substring.
          if (!cellText.includes(raw.trim().toLowerCase())) return false;
        }
      }
      return true;
    });

    if (sort.key) {
      const col = colByKey.get(sort.key);
      if (col) {
        const dir = sort.dir === 'asc' ? 1 : -1;
        // Stable sort: decorate with the original index, compare, restore order.
        filtered
          .map((row, i) => [row, i] as [T, number])
          .sort((a, b) => {
            const av = col.value(a[0]);
            const bv = col.value(b[0]);
            let cmp: number;
            if (typeof av === 'number' && typeof bv === 'number') {
              cmp = av - bv;
            } else {
              cmp = valueToText(av).localeCompare(valueToText(bv));
            }
            return cmp !== 0 ? cmp * dir : a[1] - b[1];
          })
          .forEach(([row], i) => {
            filtered[i] = row;
          });
      }
    }

    return filtered;
  }, [data, liveFilters, sort, colByKey]);

  const total = filteredSorted.length;

  const rows = useMemo(() => {
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const current = Math.min(base.page, pageCount);
    const start = (current - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, total, pageSize, base.page]);

  // Override `facetValues` so a facet with no explicit options uses the derived
  // set learned from the loaded rows.
  const facetValues = useCallback(
    (key: string): string[] => {
      const col = colByKey.get(key);
      if (col?.facetOptions) return col.facetOptions;
      return derivedFacets.get(key) ?? base.facetValues(key);
    },
    [colByKey, derivedFacets, base],
  );

  return { ...base, facetValues, rows, total };
}

/**
 * Serialize the hook's `params` into the admin list query string fragment the
 * Phase-1 backend reads: `sort=key:dir`, `filter.<col>=value` (text) /
 * comma-joined (facets), `limit`, `offset`. Returns a `URLSearchParams` so a
 * page can add its own params (e.g. `search`, `has_no_brand`) before stringifying.
 */
export function buildAdminListQuery(params: AdminQueryParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.sort.key) {
    sp.set('sort', `${params.sort.key}:${params.sort.dir}`);
  }
  for (const [key, value] of Object.entries(params.filters)) {
    if (Array.isArray(value)) {
      const joined = value.filter((v) => v.trim() !== '').join(',');
      if (joined !== '') sp.set(`filter.${key}`, joined);
    } else if (typeof value === 'string' && value.trim() !== '') {
      sp.set(`filter.${key}`, value.trim());
    }
  }
  sp.set('limit', String(params.limit));
  sp.set('offset', String(params.offset));
  return sp;
}
